import { useCallback } from 'react';

import { BookMarkedIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import Tile from '@/components/organisms/Tile/Tile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AIAgentIcon } from '@/modules/aiAgents/icons/AIAgentIcon';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Brain } from '@/shared/types/brain';
import { COLLECTION_LANGUAGES } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';
import { preventClickThrough } from '@/util/util';

import styles from './BrainTile.module.scss';

interface Props {
  brain?: Brain;
  brain_version?: number;
  collection_name?: string;
  collection_id?: string;
}
const BrainTile = ({ brain, brain_version, collection_id, collection_name }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const lastTrained = brain?.last_trained;
  const features = useSelector(selectFeatureFlags);

  const enableBrainCollections =
    !features?.enable_collections_for_rules && COLLECTION_LANGUAGES.includes(brain?.language);

  const handleCollectionClick = useCallback(
    (e) => {
      preventClickThrough(e);
      if (collection_id) {
        navigate(`/${slug}/knowledge-bases/${collection_id}`);
      } else {
        navigate(`/${slug}/ai-agents/${brain?.brain_id}/guidelines`);
      }
    },
    [brain?.brain_id, navigate, slug, collection_id]
  );

  if (!brain?.brain_parent_id) {
    return (
      <Tile className={styles.tile}>
        <div className={styles.wrapper}>
          <Button variant="ghost">{t('brains.add_brain')}</Button>
        </div>
      </Tile>
    );
  }

  return (
    <Link to={`/${slug}/ai-agents/${brain?.brain_parent_id}`}>
      <Tile className={styles.tile}>
        <div className={styles.wrapper}>
          <AIAgentIcon type={brain?.brain_type} />
          <div className={styles.content}>
            <div className={styles.info}>
              <div className={styles.header}>
                <span className="text-sm leading-5 font-semibold text-foreground">
                  {brain?.name}
                </span>{' '}
                <span
                  className={cn('text-sm leading-5 font-normal text-foreground', styles.country)}
                >{`(${brain?.language})`}</span>
              </div>

              <span className="pr-1 text-xs leading-5 font-medium text-foreground-muted">
                {t('brains.last_trained')}:{' '}
                <span className="font-regular text-xs leading-5 text-foreground">
                  {lastTrained ? (
                    <TimestampTooltip timestamp={lastTrained}>
                      {timeFromNow(lastTrained)}
                    </TimestampTooltip>
                  ) : (
                    '-'
                  )}
                </span>
              </span>
            </div>
            <div className={styles.status}>
              <Badge type="secondary" flavour="informational">
                {brain_version === 0
                  ? t('common.draft')
                  : t('common.version_with_number', { 0: brain_version })}
              </Badge>
            </div>
          </div>
        </div>

        {enableBrainCollections && (
          <Button
            className={styles.collectionWrapper}
            onClick={handleCollectionClick}
            variant="ghost"
          >
            <span className="flex gap-2 text-sm leading-none font-semibold text-accent">
              {collection_id ? (
                <>
                  <BookMarkedIcon size={16} className="text-accent" />
                  {collection_name}
                </>
              ) : (
                <>{t('collections.connect_collection')}</>
              )}
            </span>
          </Button>
        )}
      </Tile>
    </Link>
  );
};

export default BrainTile;
